<template>
  <v-row dense>
    <template v-for="(field, index) in fields">
      <template v-if="!field.hidden || !field.hidden(localValue)">
        <template v-if="field.items">
          <template v-if="field.search">
            <v-col
              :key="index"
              :cols="field.cols || 12"
              class="mb-2"
            >
              <v-row dense>
                <v-col
                  class="labeled-input"
                  cols="12"
                >
                  {{ $bct(field.text) }}

                  <RequiredIndicator v-if="fieldRequired(field)" />
                </v-col>

                <v-col cols="12">
                  <v-autocomplete
                    v-model="localValue[field.value]"
                    @update:search="emit('search', [field.value, $event])"
                    @vue:mounted="emit('search', [field.value, ''])"
                    :aria-label="field.text"
                    :chips="field.multiple"
                    :closable-chips="field.multiple"
                    :data-cy="field.dataCy || 'resource-form-search'"
                    :data-testid="field.dataTestId || 'resource-form-search'"
                    :dense="dense"
                    :item-title="field.itemText"
                    :item-value="field.itemValue"
                    :items="field.items"
                    variant="filled"
                    auto-select-first
                    clearable
                    hide-details
                  />
                </v-col>
              </v-row>
            </v-col>
          </template>

          <LabeledAutocomplete
            v-else
            v-model="localValue[field.value]"
            @change="emit('change', [field.value, $event])"
            :key="index"
            :chips="field.multiple"
            :cols="field.cols"
            :data-cy="field.dataCy || 'resource-form-select'"
            :deletable-chips="field.multiple"
            :dense="dense"
            :description="field.description"
            :hard-lock="fieldDisabled(field)"
            :input-test-id="field.dataTestId"
            :item-title="field.itemText"
            :item-value="field.itemValue"
            :items="Array.isArray(field.items) ? field.items : field.items(localValue)"
            :mandatory="fieldRequired(field)"
            :message="field.text"
            :multiple="field.multiple"
            :placeholder="field.placeholder"
            class="mb-2"
            clearable
          />
        </template>

        <template v-else>
          <LabeledCombobox
            v-if="field.multiple"
            v-model="localValue[field.value]"
            :key="index"
            :cols="field.cols"
            :data-cy="field.dataCy || 'resource-form-combo'"
            :dense="dense"
            :description="field.description"
            :message="field.text"
            class="mb-2"
            chips
            deletable-chips
            multiple
          />

          <template v-else>
            <LabeledCheckbox
              v-if="field.type == 'boolean'"
              v-model="localValue[field.value]"
              :key="index"
              :checkbox-text="field.checkboxText"
              :data-cy="field.dataCy || 'resource-form-checkbox'"
              :dense="dense"
              :description="field.description"
              :false-value="false"
              :mandatory="fieldRequired(field)"
              :message="field.text"
              :true-value="true"
              hide-details
            />

            <LabeledSwitch
              v-if="field.type == 'switch'"
              v-model="localValue[field.value]"
              :key="index"
              :data-cy="field.dataCy || 'resource-form-switch'"
              :dense="dense"
              :hard-lock="field.hardLock"
              :hard-lock-message="field.hardLockMessage"
              :subtitle="field.description || ''"
              :title="field.text"
              class="mb-2"
              cols="12"
            />

            <LabeledColorSelect
              v-if="field.type == 'color-hex'"
              v-model="localValue[field.value]"
              :key="index"
              :cols="field.cols"
              :data-cy="field.dataCy || 'resource-form-color-field'"
              :dense="dense"
              :mandatory="fieldRequired(field)"
              :message="field.text"
            />

            <LabeledCurrencyInput
              v-if="field.type == 'currency'"
              v-model="localValue[field.value]"
              :key="index"
              :cols="field.cols"
              :data-cy="field.dataCy || 'resource-form-currency'"
              :dense="dense"
              :description="field.description"
              :mandatory="fieldRequired(field)"
              :message="field.text"
              class="mb-2"
            />

            <LabeledDatePicker
              v-if="field.type == 'date'"
              v-model="localValue[field.value]"
              :key="index"
              :cols="field.cols"
              :data-cy="field.dataCy || 'resource-form-date'"
              :dense="dense"
              :description="field.description"
              :disallow-before-date="field.disallowBeforeDate"
              :mandatory="fieldRequired(field)"
              :message="field.text"
              class="mb-2"
            />

            <LabeledDateOfBirth
              v-if="field.type == 'dob'"
              v-model="localValue[field.value]"
              :key="index"
              :cols="field.cols"
              :data-cy="field.dataCy || 'resource-form-dob'"
              :dense="dense"
              :description="field.description"
              :mandatory="fieldRequired(field)"
              :message="field.text"
              class="mb-2"
            />

            <LabeledHoursAndMinutes
              v-if="field.type == 'hours-and-minutes'"
              v-model="localValue[field.value]"
              :key="index"
              :cols="field.cols"
              :data-cy="field.dataCy || 'resource-form-hours-and-minutes'"
              :dense="dense"
              :description="field.description"
              :mandatory="fieldRequired(field)"
              :message="field.text"
              class="mb-2"
            />

            <LabeledNumber
              v-if="field.type == 'number'"
              v-model="localValue[field.value]"
              :key="index"
              :cols="field.cols"
              :data-cy="field.dataCy || 'resource-form-number'"
              :dense="dense"
              :description="field.description"
              :integer-only="field.integerOnly === true"
              :mandatory="fieldRequired(field)"
              :max="field.max"
              :message="field.text"
              :min="field.min"
              :step="field.step"
              class="mb-2"
            />

            <LabeledRichTextEditor
              v-if="field.type == 'richtext'"
              v-model="localValue[field.value]"
              :key="index"
              :cols="field.cols"
              :data-testid="field.dataCy || 'resource-form-richtext'"
              :height="field.height"
              :mandatory="fieldRequired(field)"
              :message="field.text"
              class="mb-2"
            />

            <template v-if="['password', 'string'].includes(field.type) || !field.type">
              <LabeledTextarea
                v-if="field.rows"
                v-model="localValue[field.value]"
                :key="index"
                :cols="field.cols"
                :data-cy="field.dataCy || 'resource-form-textarea'"
                :dense="dense"
                :mandatory="fieldRequired(field)"
                :message="field.text"
                :rows="field.rows"
                class="mb-2"
              />

              <template v-else>
                <LabeledTextfield
                  v-if="field.protected"
                  v-model="localValue[field.value]"
                  @copy.prevent
                  @paste.prevent
                  :key="index + 'protected'"
                  :cols="field.cols"
                  :data-cy="field.dataCy || 'resource-form-text-field'"
                  :data-testid="field.dataTestid"
                  :dense="dense"
                  :description="field.description"
                  :extra-rules="field.rules || []"
                  :hard-lock="fieldDisabled(field)"
                  :mandatory="fieldRequired(field)"
                  :mask="field.mask"
                  :message="field.text"
                  :readonly="fieldReadonly(field)"
                  :type="field.type"
                  class="mb-2"
                />

                <LabeledTextfield
                  v-else
                  v-model="localValue[field.value]"
                  :key="index"
                  :cols="field.cols"
                  :data-cy="field.dataCy || 'resource-form-text-field'"
                  :data-testid="field.dataTestid"
                  :dense="dense"
                  :description="field.description"
                  :extra-rules="field.rules || []"
                  :hard-lock="fieldDisabled(field)"
                  :mandatory="fieldRequired(field)"
                  :mask="field.mask"
                  :message="field.text"
                  :placeholder="field.placeholder"
                  :readonly="fieldReadonly(field)"
                  :type="field.type"
                  class="mb-2"
                />
              </template>
            </template>

            <LabeledTextarea
              v-if="field.type == 'margin'"
              v-model="localValue[field.value]"
              :key="index"
              :cols="field.cols"
              :data-cy="field.dataCy || 'resource-form-textarea'"
              :dense="dense"
              :description="field.description"
              :mandatory="fieldRequired(field)"
              :mask="field.mask"
              :message="field.text"
              class="mb-2"
            />
          </template>
        </template>
      </template>
    </template>
  </v-row>
</template>

<script setup>
import LabeledAutocomplete from '@/shared/components/form/LabeledAutocomplete.vue';
import LabeledCheckbox from '@/shared/components/form/LabeledCheckbox.vue';
import LabeledColorSelect from '@/shared/components/form/LabeledColorSelect.vue';
import LabeledCombobox from '@/shared/components/form/LabeledCombobox.vue';
import LabeledCurrencyInput from '@/shared/components/form/LabeledCurrencyInput.vue';
import LabeledDateOfBirth from '@/shared/components/form/LabeledDateOfBirth.vue';
import LabeledDatePicker from '@/shared/components/form/LabeledDatePicker.vue';
import LabeledHoursAndMinutes from '@/shared/components/form/LabeledHoursAndMinutes.vue';
import LabeledNumber from '@/shared/components/form/LabeledNumber.vue';
import LabeledRichTextEditor from '@/shared/components/form/LabeledRichTextEditor.vue';
import LabeledSwitch from '@/shared/components/form/LabeledSwitch.vue';
import LabeledTextarea from '@/shared/components/form/LabeledTextarea.vue';
import LabeledTextfield from '@/shared/components/form/LabeledTextfield.vue';
import RequiredIndicator from '@/shared/components/RequiredIndicator.vue';

const $bct = inject('translator');

const localValue = defineModel({ type: Object });

const emit = defineEmits(['change', 'search']);

function fieldRequired(field) {
  if (typeof field.required === 'function') {
    return field.required(localValue.value);
  }

  return !!field.required;
}

function fieldReadonly(field) {
  if (typeof field.readonly === 'function') {
    return field.readonly(localValue.value);
  }

  return !!field.readonly;
}

function fieldDisabled(field) {
  if (typeof field.disabled === 'function') {
    return field.disabled(localValue.value);
  }

  return !!field.disabled;
}

defineProps({
  fields: {
    type: Array,
    default() {
      return [];
    },
  },
  dense: {
    type: Boolean,
    default: undefined,
  },
});
</script>
